import { type CSSProperties, type ReactNode } from 'react';
import { darkTheme, FONT } from '../../../theme/tokens';
import { Badge } from '../../ui/Badge';
import { HoverHint } from '../../ui/HoverHint';
import { KEPT_AS_IS_NOTE, KEPT_AS_IS_TOOLTIP } from './opDisplay';

const t = darkTheme;

// Shared building blocks across per-kind bodies. Keeping them in one file so
// the per-kind components stay focused on layout decisions rather than styling
// primitives.

interface ScopeRowProps {
  children: ReactNode;
}

/** Top row of a body — scope indicators and field labels. */
export function ScopeRow({ children }: ScopeRowProps) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        flexWrap: 'wrap',
        marginBottom: 12,
      }}
    >
      {children}
    </div>
  );
}

interface FieldLabelProps {
  children: ReactNode;
}

/** Renders a column/field name as a muted code-style chip. `overflowWrap` gives
 *  long unbroken identifiers (snake_case catalog columns) a break opportunity —
 *  without it a chip's min-content is the full word, which defeats any width cap
 *  its container sets (the SC-2901 grid's `fit-content(50%)` field column). */
export function FieldLabel({ children }: FieldLabelProps) {
  return (
    <span
      style={{
        background: t.surfaceAlt,
        color: t.text2,
        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
        fontSize: 12,
        padding: '3px 10px',
        borderRadius: 4,
        border: `1px solid ${t.border}`,
        overflowWrap: 'anywhere',
      }}
    >
      {children}
    </span>
  );
}

type ActionVerb = 'replace' | 'fill' | 'remove' | 'flag_for_review';

interface ValueDiffProps {
  verb: ActionVerb;
  current: string | null;
  proposed: string | null;
  /** A SIBLING op makes a real change, so a confirmed-unchanged value reads as
   *  the kept half of a paired repair (SC-2799 polish), not a lone confirmation. */
  pairedChange?: boolean;
}

/** Past this many characters a side-by-side diff clips both values into
 *  ellipsis soup — switch to a stacked before ↓ after layout (mirroring the
 *  entity drawer) and let the text wrap instead. */
const STACK_AT = 40;

/**
 * Renders the verb-specific current → proposed diff. Shared between
 * EntityAction and BulkEntityAction bodies (4 verbs each, same layout pattern).
 */
export function ValueDiff({ verb, current, proposed, pairedChange = false }: ValueDiffProps) {
  const long = (current?.length ?? 0) > STACK_AT || (proposed?.length ?? 0) > STACK_AT;
  if (verb === 'flag_for_review') {
    return (
      <div style={diffRowStyle}>
        <ValueChip text={current} wrap={long} />
        <span style={{ color: t.amberText, fontSize: 12, fontWeight: 500 }}>
          · Needs your input
        </span>
      </div>
    );
  }
  // before === after asserts "this value is already correct" (the paired half of a
  // multi-field repair) — render it as confirmed, not as a diff of identical strings
  // (SC-2799). Keep it visible: the value was considered and deliberately kept.
  // Equal empties count too: '' → '' asserts "deliberately blank", not a change.
  if (current != null && current === proposed) {
    const note = (
      <span style={{ color: t.text3, fontSize: 12, fontWeight: 500 }}>
        · {pairedChange ? KEPT_AS_IS_NOTE : 'unchanged (confirmed correct)'}
      </span>
    );
    return (
      <div style={diffRowStyle}>
        <ValueChip text={current} wrap={long} />
        {pairedChange ? <HoverHint hint={KEPT_AS_IS_TOOLTIP}>{note}</HoverHint> : note}
      </div>
    );
  }
  const before = (
    <ValueChip
      text={current}
      placeholder={verb === 'fill' ? '<empty>' : undefined}
      muted={verb === 'fill'}
      wrap={long}
    />
  );
  const after = (
    <ValueChip
      text={proposed}
      placeholder={verb === 'remove' ? '<clear>' : undefined}
      muted={verb === 'remove'}
      wrap={long}
    />
  );
  if (long) {
    return (
      <div data-stacked-diff style={stackedDiffStyle}>
        {before}
        <span aria-hidden="true" style={{ color: t.text3, fontSize: 13, lineHeight: 1 }}>
          ↓
        </span>
        {after}
      </div>
    );
  }
  return (
    <div style={diffRowStyle}>
      {before}
      <Arrow />
      {after}
    </div>
  );
}

const diffRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  flexWrap: 'wrap',
  fontFamily: FONT,
  marginBottom: 4,
};

const stackedDiffStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: 4,
  fontFamily: FONT,
  marginBottom: 4,
};

interface ValueChipProps {
  text: string | null;
  placeholder?: string;
  muted?: boolean;
  /** Long-value mode: wrap the full text instead of clipping to one ellipsized line. */
  wrap?: boolean;
}

export function ValueChip({ text, placeholder, muted, wrap }: ValueChipProps) {
  const display = text == null || text === '' ? (placeholder ?? '<empty>') : text;
  const isPlaceholder = text == null || text === '' || muted;
  return (
    <span
      style={{
        background: isPlaceholder ? 'transparent' : t.surfaceAlt,
        color: isPlaceholder ? t.text3 : t.text,
        fontStyle: isPlaceholder ? 'italic' : 'normal',
        fontSize: 13,
        padding: '4px 10px',
        borderRadius: 4,
        border: isPlaceholder ? `1px dashed ${t.border2}` : `1px solid ${t.border}`,
        maxWidth: '100%',
        ...(wrap
          ? { whiteSpace: 'normal' as const, overflowWrap: 'anywhere' as const }
          : { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const }),
      }}
      title={wrap ? undefined : (text ?? undefined)}
    >
      {isPlaceholder ? display : `"${display}"`}
    </span>
  );
}

function Arrow() {
  return (
    <span style={{ color: t.text3, fontSize: 14, fontWeight: 500 }} aria-hidden="true">
      →
    </span>
  );
}

/**
 * A monospace value chip — a rename target, a transform value, a field header.
 * Larger and less muted than {@link FieldLabel}; used by the ChangeSet op
 * previews (SC-2524).
 */
export function CodeChip({ children }: { children: ReactNode }) {
  return (
    <code
      style={{
        background: t.surfaceAlt,
        color: t.text,
        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
        fontSize: 13,
        padding: '4px 10px',
        borderRadius: 4,
        border: `1px solid ${t.border}`,
      }}
    >
      {children}
    </code>
  );
}

interface ChipListProps {
  items: string[];
}

/** Renders a list of values as small chips (used by FieldAction split, CatalogAction locked_fields). */
export function ChipList({ items }: ChipListProps) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
      {items.map((item, i) => (
        // `${item}-${i}` rather than `item` alone — defends against a
        // misbehaving BE that returns duplicate values in split_into /
        // proposed_values (which would otherwise warn from React).
        <Badge key={`${item}-${i}`} tone="info">
          {item}
        </Badge>
      ))}
    </div>
  );
}

